// Normalizers that turn API responses into flat row sets suitable for CSV/Excel/PDF export.
// Each function returns: rows (array of objects) + a `columns` ordering so headers stay stable.

import type {
  CasesBreakdown,
  CasesBreakdownEntry,
  CasesTimeByStep,
  CasesTrendPoint,
  SlaComplianceTrendPoint,
  TimeToSubmitPoint,
} from '@compass/client/types/cases_analytics';

import type {
  CallBreakdown,
  CallVolumeTrendPoint,
  ComplianceTrendPoint,
  EscalationTrendPoint,
  OrgSummary,
  PerformanceRadar,
  QuestionRate,
  RadarPoint,
  ScoreTrendPoint,
} from '@/pages/analytics/types';
import { HOUR_BUCKET_STARTS } from '@/pages/cases-analytics/utils/constants';
import type { Grouping } from '@/pages/cases-analytics/utils/groupings';
import { hourBucketLabel } from '@/pages/cases-analytics/utils/hourBuckets';
import { STEP_LABELS, STEP_TIER, summarizeSteps } from '@/pages/cases-analytics/utils/steps';
import { SCORE_BAND_EXEMPLARY_MIN, SCORE_BAND_MEETS_MIN } from '@/pages/qc/utils';

import { formatChartAxisDate } from './chartDate';
import { formatDurationSeconds } from './formatters';
import { EMPTY_QUESTION_CATEGORY_LABEL } from './questionRateTabs';
import { deriveRadarAxesFromPoints } from './radarAxes';

export interface NormalizedExport {
  filenameStem: string;
  columns: string[];
  rows: Record<string, string | number>[];
}

type ExportScope = 'overall' | 'by_program';

// Accepts undefined so callers can pipe through optional MetricWithPrior.prior_value.
const toPercent = (v: number | undefined, digits = 1): string =>
  v != null && Number.isFinite(v) ? `${(v * 100).toFixed(digits)}%` : '';
const toPercentNumber = (v: number): number => Number((v * 100).toFixed(1));
const formatExportQuestionCategory = (category: string): string =>
  category.trim() ? category : EMPTY_QUESTION_CATEGORY_LABEL;
const toOptionalPercentNumber = (v: number | undefined): number | string =>
  v != null && Number.isFinite(v) ? toPercentNumber(v) : '';
const isVisibleGroup = (group: string, scope: ExportScope): boolean =>
  scope === 'overall' ? group === 'overall' : group !== 'overall';

// Backend ships radar values in the `categories` map; the duplicate flat
// fields are "backward compat" with no consumer and are ignored.
const radarAxis = (p: RadarPoint, key: string): number => p.categories?.[key] ?? 0;

const average = (values: number[]): number | undefined =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : undefined;

export const normalizeScoreTrend = (
  data: ScoreTrendPoint[],
  scope: 'overall' | 'by_program' = 'overall',
): NormalizedExport => {
  const visibleRows = data
    .filter(p => (scope === 'overall' ? p.group === 'overall' : p.group !== 'overall'))
    .sort((a, b) => a.date.localeCompare(b.date) || a.group_name.localeCompare(b.group_name));

  if (scope === 'overall') {
    return {
      filenameStem: 'score_trend',
      columns: ['Period', 'Average Score (%)', 'Calls Evaluated'],
      rows: visibleRows.map(p => ({
        Period: formatChartAxisDate(p.date),
        'Average Score (%)': toPercentNumber(p.avg_score),
        'Calls Evaluated': p.total_evaluations,
      })),
    };
  }

  const programColumnNames = [...new Map(visibleRows.map(p => [p.group, p.group_name])).values()];

  return {
    filenameStem: 'score_trend',
    columns: ['Period', ...programColumnNames.map(name => `${name} (%)`)],
    rows: [...new Set(visibleRows.map(p => p.date))].map(date => {
      const periodRows = visibleRows.filter(p => p.date === date);
      const row: Record<string, string | number> = {
        Period: formatChartAxisDate(date),
        ...Object.fromEntries(programColumnNames.map(name => [`${name} (%)`, ''])),
      };

      for (const point of periodRows) {
        row[`${point.group_name} (%)`] = toPercentNumber(point.avg_score);
      }

      return row;
    }),
  };
};

export const normalizeRadar = (
  data: PerformanceRadar,
  scope: ExportScope = 'overall',
): NormalizedExport => {
  if (scope === 'overall') {
    const current = data.current_period.find(p => p.group === 'overall');
    const prior = data.prior_period?.find(p => p.group === 'overall');
    // Sections come from the current period only; prior is the comparison
    // column and may carry stray non-section keys for wider windows. See LA-5304.
    const axes = deriveRadarAxesFromPoints(current ? [current] : []);

    return {
      filenameStem: 'performance_radar',
      columns: ['Section', 'Current Period (%)', 'Prior Period (%)'],
      rows: axes.map(axis => ({
        Section: axis.label,
        'Current Period (%)': toOptionalPercentNumber(
          current ? radarAxis(current, axis.key) : undefined,
        ),
        'Prior Period (%)': toOptionalPercentNumber(prior ? radarAxis(prior, axis.key) : undefined),
      })),
    };
  }

  const current = data.current_period
    .filter(p => isVisibleGroup(p.group, scope))
    .sort((a, b) => a.group_name.localeCompare(b.group_name));
  const prior = (data.prior_period ?? []).filter(p => isVisibleGroup(p.group, scope));
  // Sections from the current period only (see LA-5304 and the overall branch).
  const axes = deriveRadarAxesFromPoints(current);
  const programColumns = current.map(p => `${p.group_name} (%)`);

  return {
    filenameStem: 'performance_radar',
    columns: ['Section', 'Prior Period (%)', ...programColumns],
    rows: axes.map(axis => {
      const row: Record<string, string | number> = {
        Section: axis.label,
        'Prior Period (%)': toOptionalPercentNumber(
          average(prior.map(p => radarAxis(p, axis.key))),
        ),
      };

      for (const point of current) {
        row[`${point.group_name} (%)`] = toPercentNumber(radarAxis(point, axis.key));
      }

      return row;
    }),
  };
};

// scope 'overall' exports the single aggregate series (one Calls column);
// 'by_program' pivots to a wide Period × program-name grid (one column per program).
export const normalizeCallVolumeTrend = (
  data: CallVolumeTrendPoint[],
  scope: ExportScope = 'overall',
): NormalizedExport => {
  const visibleRows = data
    .filter(p => isVisibleGroup(p.group, scope))
    .sort((a, b) => a.date.localeCompare(b.date) || a.group_name.localeCompare(b.group_name));

  if (scope === 'overall') {
    return {
      filenameStem: 'call_volume',
      columns: ['Period', 'Calls'],
      rows: visibleRows.map(p => ({
        Period: formatChartAxisDate(p.date),
        Calls: p.call_count,
      })),
    };
  }

  const programColumnNames = [...new Map(visibleRows.map(p => [p.group, p.group_name])).values()];

  return {
    filenameStem: 'call_volume',
    columns: ['Period', ...programColumnNames],
    rows: [...new Set(visibleRows.map(p => p.date))].map(date => {
      const periodRows = visibleRows.filter(p => p.date === date);
      const row: Record<string, string | number> = {
        Period: formatChartAxisDate(date),
        ...Object.fromEntries(programColumnNames.map(name => [name, ''])),
      };
      for (const point of periodRows) {
        row[point.group_name] = point.call_count;
      }
      return row;
    }),
  };
};

export const normalizeComplianceTrend = (
  data: ComplianceTrendPoint[],
  scope: ExportScope = 'overall',
): NormalizedExport => {
  const visibleRows = data
    .filter(p => isVisibleGroup(p.group, scope))
    .sort((a, b) => a.date.localeCompare(b.date) || a.group_name.localeCompare(b.group_name));

  if (scope === 'overall') {
    return {
      filenameStem: 'compliance_failure_rate',
      columns: ['Period', 'Rate (%)', 'Count', 'Total Calls'],
      rows: visibleRows.map(p => ({
        Period: formatChartAxisDate(p.date),
        'Rate (%)': toPercentNumber(p.rate),
        Count: p.failed_count,
        'Total Calls': p.total_count,
      })),
    };
  }

  return {
    filenameStem: 'compliance_failure_rate',
    columns: ['Period', 'Program', 'Rate (%)', 'Count', 'Total Calls'],
    rows: visibleRows.map(p => ({
      Period: formatChartAxisDate(p.date),
      Program: p.group_name,
      'Rate (%)': toPercentNumber(p.rate),
      Count: p.failed_count,
      'Total Calls': p.total_count,
    })),
  };
};

export const normalizeEscalationTrend = (
  data: EscalationTrendPoint[],
  scope: ExportScope = 'overall',
): NormalizedExport => {
  const visibleRows = data
    .filter(p => isVisibleGroup(p.group, scope))
    .sort((a, b) => a.date.localeCompare(b.date) || a.group_name.localeCompare(b.group_name));

  if (scope === 'overall') {
    return {
      filenameStem: 'escalation_rate',
      columns: ['Period', 'Rate (%)', 'Count', 'Total Calls'],
      rows: visibleRows.map(p => ({
        Period: formatChartAxisDate(p.date),
        'Rate (%)': toPercentNumber(p.rate),
        Count: p.escalated_count,
        'Total Calls': p.total_count,
      })),
    };
  }

  return {
    filenameStem: 'escalation_rate',
    columns: ['Period', 'Program', 'Rate (%)', 'Count', 'Total Calls'],
    rows: visibleRows.map(p => ({
      Period: formatChartAxisDate(p.date),
      Program: p.group_name,
      'Rate (%)': toPercentNumber(p.rate),
      Count: p.escalated_count,
      'Total Calls': p.total_count,
    })),
  };
};

// Question pass/miss export. Unlike the chart (which renders the org-wide
// `overall` rows only), the export ALWAYS carries the per-program breakdown: a
// `Program` column plus every question repeated once per program, alongside the
// org-wide `Overall` rows. Rows are program-major — the `Overall` block first,
// then each program ordered by total evaluation volume — so a program's full
// question set stays together. Applies to CSV + Excel; PDF is a chart snapshot
// (DOM capture) and is unaffected.
export const normalizeQuestionRates = (data: QuestionRate[]): NormalizedExport => {
  // Backend ships placeholder rows for unmapped scorecard questions with a
  // blank label; drop them (matches the chart).
  const labeled = data.filter(q => q.metric_label.trim() !== '');

  // Rank programs by total evaluation volume so the busiest lead their blocks.
  const programVolume = new Map<string, number>();
  for (const q of labeled) {
    if (q.group === 'overall') continue;
    programVolume.set(q.group, (programVolume.get(q.group) ?? 0) + q.total_count);
  }

  const orderedRows = [...labeled].sort((a, b) => {
    const aOverall = a.group === 'overall';
    const bOverall = b.group === 'overall';
    // Overall block first.
    if (aOverall !== bOverall) return aOverall ? -1 : 1;
    if (!aOverall) {
      // Programs by descending volume, then name for a stable tiebreak.
      const volumeDelta = (programVolume.get(b.group) ?? 0) - (programVolume.get(a.group) ?? 0);
      if (volumeDelta !== 0) return volumeDelta;
      const nameDelta = a.group_name.localeCompare(b.group_name);
      if (nameDelta !== 0) return nameDelta;
    }
    // Within a block: section, then heaviest questions first, then label.
    return (
      a.category.localeCompare(b.category) ||
      b.total_count - a.total_count ||
      a.metric_label.localeCompare(b.metric_label)
    );
  });

  return {
    filenameStem: 'question_pass_rates',
    columns: [
      'Title',
      'Section',
      'Program',
      'Passed',
      'Missed',
      'Total Evaluations',
      'Pass Rate (%)',
      'Miss Rate (%)',
    ],
    rows: orderedRows.map(q => ({
      Title: q.metric_label,
      Section: formatExportQuestionCategory(q.category),
      Program: q.group === 'overall' ? 'Overall' : q.group_name,
      Passed: q.passed_count,
      Missed: q.total_count - q.passed_count,
      'Total Evaluations': q.total_count,
      'Pass Rate (%)': toPercentNumber(q.pass_rate),
      'Miss Rate (%)': toPercentNumber(1 - q.pass_rate),
    })),
  };
};

// `prior_value` is optional on MetricWithPrior; render '' when absent.
const intOrBlank = (v: number | undefined): string | number => (v != null ? v : '');
const durationOrBlank = (v: number | undefined): string =>
  v != null ? formatDurationSeconds(v) : '';

export const normalizeOrgSummary = (s: OrgSummary): NormalizedExport => ({
  filenameStem: 'org_summary',
  columns: ['Metric', 'Current', 'Prior'],
  rows: [
    {
      Metric: 'Average Quality Score',
      Current: toPercent(s.average_quality_score.value),
      Prior: toPercent(s.average_quality_score.prior_value),
    },
    {
      Metric: 'Total Calls',
      Current: s.total_calls.value,
      Prior: intOrBlank(s.total_calls.prior_value),
    },
    {
      Metric: 'Evaluated Calls',
      Current: s.evaluated_calls.value,
      Prior: intOrBlank(s.evaluated_calls.prior_value),
    },
    {
      Metric: 'Average Call Duration',
      Current: formatDurationSeconds(s.average_call_duration_seconds.value),
      Prior: durationOrBlank(s.average_call_duration_seconds.prior_value),
    },
    {
      Metric: 'Escalation Rate',
      Current: toPercent(s.escalation_rate.value),
      Prior: toPercent(s.escalation_rate.prior_value),
    },
    {
      Metric: 'Compliance Failure Rate',
      Current: toPercent(s.compliance_failure_rate.value),
      Prior: toPercent(s.compliance_failure_rate.prior_value),
    },
  ],
});

interface BreakdownRowLike {
  call_count: number;
  avg_score: number;
}

const breakdownRow = <T extends BreakdownRowLike>(dimension: string, label: string, r: T) => ({
  Dimension: dimension,
  Label: label,
  'Call Count': r.call_count,
  'Average Score': toPercent(r.avg_score),
});

// TODO(AQ-516): remove hardcoded keys. Keep the call-sourced skill/campaign/team
// (AQ-437) and Genesys queue dimensions out of call-breakdown exports
// (CSV/Excel/PDF), matching the charts. Interim hardcode; replace with a
// metadata-registry-driven predicate.
// See https://100ms.atlassian.net/browse/AQ-516
const HIDDEN_METADATA_EXPORT_KEYS = new Set([
  'skill-name',
  'campaign-name',
  'team-name',
  'queue-name',
]);

export const normalizeCallBreakdown = (b: CallBreakdown): NormalizedExport => ({
  filenameStem: 'call_breakdown',
  columns: ['Dimension', 'Label', 'Call Count', 'Average Score'],
  rows: [
    ...b.by_direction.map(r => breakdownRow('Direction', r.direction || '—', r)),
    ...Object.entries(b.by_metadata ?? {})
      .filter(([dimension]) => !HIDDEN_METADATA_EXPORT_KEYS.has(dimension))
      .flatMap(([dimension, rows]) => rows.map(r => breakdownRow(dimension, r.value || '—', r))),
  ],
});

export interface AgentExportRow {
  agent_id: string;
  agent_name: string;
  avg_score: number;
  call_count: number;
  last_calls: Array<{ score: number }>;
}

const filenameToken = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'date_range';

export const normalizeAgentsList = (
  data: AgentExportRow[],
  period: string = '30d',
): NormalizedExport => ({
  filenameStem: `agents_performance_${filenameToken(period)}`,
  columns: ['Agent', 'Average Score (%)', 'Score Band', 'Last 5 Scores', 'Call Count'],
  rows: data.map(toAgentExportRow),
});

const scoreBandLabel = (score: number): string => {
  if (score >= SCORE_BAND_EXEMPLARY_MIN) return 'Exemplary';
  if (score >= SCORE_BAND_MEETS_MIN) return 'Meets Expectations';
  return 'Unsatisfactory';
};

const toAgentExportRow = (a: AgentExportRow): Record<string, string | number> => {
  const lastFiveScores = a.last_calls
    .slice(0, 5)
    .map(call => (Number.isFinite(call.score) ? (call.score * 100).toFixed(1) : ''))
    .filter(Boolean)
    .join(', ');

  return {
    Agent: a.agent_name,
    'Average Score (%)': toPercentNumber(a.avg_score),
    'Score Band': scoreBandLabel(a.avg_score),
    'Last 5 Scores': lastFiveScores,
    'Call Count': a.call_count,
  };
};

// ---- AE / PQC analytics ----------------------------------------------------
// Group names, never ids, go in the rows.

const GROUPING_COLUMN: Record<Grouping, string> = {
  by_report_type: 'Report type',
  by_program: 'Program',
  by_submission_method: 'Submission method',
};

export const normalizeSubmittedTrend = (
  points: CasesTrendPoint[],
  toggle: Grouping,
): NormalizedExport => {
  const groupColumn = GROUPING_COLUMN[toggle];
  const sorted = [...points].sort(
    (a, b) =>
      (a.date ?? '').localeCompare(b.date ?? '') ||
      (a.group_name ?? '').localeCompare(b.group_name ?? ''),
  );
  return {
    filenameStem: 'aepqc-submitted-trend',
    columns: ['Period', groupColumn, 'Cases'],
    rows: sorted.map(p => ({
      Period: formatChartAxisDate(p.date),
      [groupColumn]: p.group_name ?? p.group ?? '',
      Cases: p.case_count ?? 0,
    })),
  };
};

export const normalizeSlaCompliance = (points: SlaComplianceTrendPoint[]): NormalizedExport => {
  const sorted = [...points].sort(
    (a, b) =>
      (a.date ?? '').localeCompare(b.date ?? '') ||
      (a.group_name ?? '').localeCompare(b.group_name ?? ''),
  );
  return {
    filenameStem: 'aepqc-sla-compliance',
    columns: ['Period', 'Group', 'SLA compliance (%)', 'Within SLA', 'Total cases'],
    rows: sorted.map(p => ({
      Period: formatChartAxisDate(p.date),
      Group: p.group_name ?? p.group ?? '',
      'SLA compliance (%)': toPercentNumber(p.rate ?? 0),
      'Within SLA': p.within_sla_count ?? 0,
      'Total cases': p.total_count ?? 0,
    })),
  };
};

export const normalizeCaseBreakdown = (breakdown: CasesBreakdown): NormalizedExport => {
  const total = breakdown.total ?? 0;
  const section = (name: string, entries: CasesBreakdownEntry[] | undefined) =>
    (entries ?? []).map(e => ({
      Breakdown: name,
      Group: e.group_name ?? e.group ?? '',
      Cases: e.case_count ?? 0,
      'Share (%)': total > 0 ? toPercentNumber((e.case_count ?? 0) / total) : 0,
    }));
  return {
    filenameStem: 'aepqc-case-breakdown',
    columns: ['Breakdown', 'Group', 'Cases', 'Share (%)'],
    rows: [
      ...section('Program', breakdown.by_program),
      ...section('Submission method', breakdown.by_submission_method),
      ...section('Report type', breakdown.by_report_type),
    ],
  };
};

export const normalizeTimeToSubmit = (
  points: TimeToSubmitPoint[],
  toggle: Grouping,
): NormalizedExport => {
  const groupColumn = GROUPING_COLUMN[toggle];
  const sorted = [...points].sort(
    (a, b) =>
      (a.bucket_start_hours ?? 0) - (b.bucket_start_hours ?? 0) ||
      (a.group_name ?? '').localeCompare(b.group_name ?? ''),
  );
  return {
    filenameStem: 'aepqc-time-to-submit',
    columns: ['Time to submit', groupColumn, 'Cases'],
    rows: sorted.map(p => ({
      'Time to submit': hourBucketLabel(p.bucket_start_hours ?? 0, HOUR_BUCKET_STARTS),
      [groupColumn]: p.group_name ?? p.group ?? '',
      Cases: p.case_count ?? 0,
    })),
  };
};

// The Summary tab: average seconds per step and segment, then the two tier totals.
export const normalizeStepTime = (timeByStep: CasesTimeByStep): NormalizedExport => {
  const { rows, tier1, tier2 } = summarizeSteps(timeByStep.summary);
  const seconds = (v: number | undefined): number | string => v ?? '';
  const line = (
    step: string,
    tier: 1 | 2,
    v: { all?: number; within_sla?: number; breached?: number },
  ) => ({
    Step: step,
    Tier: tier,
    'All (seconds)': seconds(v.all),
    'Within SLA (seconds)': seconds(v.within_sla),
    'Breached (seconds)': seconds(v.breached),
  });
  return {
    filenameStem: 'aepqc-time-per-step',
    columns: ['Step', 'Tier', 'All (seconds)', 'Within SLA (seconds)', 'Breached (seconds)'],
    rows: [
      ...rows.map(r => line(STEP_LABELS[r.step], STEP_TIER[r.step], r)),
      line('Tier 1 total', 1, tier1),
      line('Tier 2 total', 2, tier2),
    ],
  };
};
